import { Injectable } from '@angular/core';
import type { Messaging } from 'firebase/messaging';
import { environment } from 'src/environments/environment';
import { UserDeviceTokenService } from '../proxy/user-device-tokens/user-device-token.service';

@Injectable({
  providedIn: 'root',
})
export class FirebaseMessagingService {
  private messaging: Messaging | null = null;
  private lastToken: string | null = null;
  private lastRowId: string | null = null;
  private readonly defaultNotificationIcon = '/assets/img/company-logo.png';

  constructor(private userDeviceTokenService: UserDeviceTokenService) {}

  getCurrentToken(): string | null {
    return this.lastToken;
  }

  private async ensureMessaging(): Promise<Messaging> {
    if (this.messaging) return this.messaging;

    const [{ initializeApp, getApps }, { getMessaging }] = await Promise.all([
      import('firebase/app'),
      import('firebase/messaging'),
    ]);

    if (!getApps().length) {
      initializeApp(environment.firebase);
    }

    this.messaging = getMessaging();
    return this.messaging;
  }

  async requestAndSaveToken() {
    try {
      const permission = await Notification.requestPermission();

      if (permission !== 'granted') {
        console.warn('Notification permission not granted');
        return;
      }

      const registration = await navigator.serviceWorker.ready;
      const messaging = await this.ensureMessaging();
      const { getToken } = await import('firebase/messaging');

      const token = await getToken(messaging, {
        vapidKey: environment.firebase.vapidKey,
        serviceWorkerRegistration: registration,
      });

      if (token) {
        this.lastToken = token;
        this.saveTokenToBackend(token);
      } else {
        console.warn('No FCM token returned');
      }

      await this.listenForeground();
    } catch (error) {
      console.error('FCM error:', error);
    }
  }

  private saveTokenToBackend(token: string) {
    const payload = {
      tokens: token,
      device: navigator.platform,
      browser: navigator.userAgent,
    };

    this.userDeviceTokenService.create(payload).subscribe({
      next: (res) => {
        this.lastToken = res.tokens;
        this.lastRowId = res.id;
      },
      error: (err) => {
        console.error('Error saving token:', err);
      },
    });
  }

  private async listenForeground() {
    const [{ onMessage }, messaging] = await Promise.all([
      import('firebase/messaging'),
      this.ensureMessaging(),
    ]);

    onMessage(messaging, (payload) => {
      const n = payload.notification;
      if (!n) return;

      new Notification(n.title ?? '', {
        body: n.body ?? '',
        icon: payload.data?.icon || this.defaultNotificationIcon,
      });
    });
  }
}
